
import { nanoid } from 'nanoid';
import { supabase, invokeFunction } from '@/integrations/supabase/client';

export const createDatabaseForCsv = async (file: File, connectionId: string): Promise<void> => {
  try {
    // Use the connectionId from the file if available (from CSV import)
    // This should now contain the full unique import ID including the base connectionId
    const fileConnectionId = (file as any).connectionId || connectionId;

    // Extract the base connection ID to ensure we use a consistent database name for all imports
    const baseConnectionId = extractBaseConnectionId(fileConnectionId);

    // Read the CSV content
    const csvContent = await file.text();

    if (!csvContent || csvContent.trim() === '') {
      throw new Error('CSV file is empty or could not be read');
    }

    // Parse the CSV to extract headers and data, with improved header detection
    const { data: parsedData, metadata } = parseCSV(csvContent);

    if (parsedData.length === 0) {
      throw new Error('No valid data found in CSV file');
    }

    try {
      const response = await invokeFunction(supabase, 'store-csv-import', {
        body: {
          connectionId: fileConnectionId, // Use the full unique import ID
          fileName: file.name,
          content: {}
        }
      });

      if (response.error) {
        console.error('Error storing CSV data via edge function:', response.error);
        throw new Error(`Failed to store CSV data: ${response.error.message}`);
      }
      if (!response.data?.success) {
        console.error('Edge function returned failure for CSV storage:', response.data);
        throw new Error(`Failed to store CSV data: ${response.data?.error || 'Unknown error'}`);
      }
    } catch (insertError) {
      console.error('Exception during CSV storage:', insertError);
      throw insertError;
    }

    // Create a consistent database name for the connection using the BASE connection ID
    // We want to reuse the same database for multiple CSVs from the same company
    const dbName = `figabl_${baseConnectionId.replace(/[^a-zA-Z0-9]/g, '_')}`;

    // Use the edge function with properly formatted JSON payload
    const response = await invokeFunction(supabase, 'create-csv-database-v2', {
      body: {
        dbName,
        connectionId: baseConnectionId, // Pass the base connection ID for database creation
        importId: fileConnectionId, // Pass the full import ID for reference
        csvData: parsedData,
        fileName: file.name,
        csvMetadata: metadata
      }
    });

    // Check for errors in the response
    if (response.error) {
      throw new Error(`Edge function error: ${response.error.message || 'Unknown error'}`);
    }

    if (!response.data || !response.data.success) {
      throw new Error(`Database creation failed: ${response.data?.error || 'Unknown error'}`);
    }

    // Store metadata about the created database
    const csvMetadata = {
      dbName,
      fileName: file.name,
      columnNames: Object.keys(parsedData[0] || {}),
      recordCount: parsedData.length,
      importDate: new Date().toISOString(),
      tableSchema: 'public',
      tableName: file.name.replace(/\.[^/.]+$/, "").toLowerCase().replace(/[^a-z0-9]/g, '_'),
      description: metadata.description
    };

    // Store in sessionStorage
    sessionStorage.setItem(`csv_db_${connectionId}`, JSON.stringify(csvMetadata));

    // Also update the connections object in sessionStorage if it exists
    const companyData = sessionStorage.getItem('figabl_company_data');
    if (companyData) {
      try {
        const parsedCompanyData = JSON.parse(companyData);
        if (parsedCompanyData?.connections) {
          // Update the existing connection with the new CSV metadata
          // Store the CSV imports as an array instead of overwriting
          const existingConnections = parsedCompanyData.connections || [];

          const updatedConnection = {
            id: baseConnectionId,
            name: "CSV Import",
            type: "CSV",
            status: "active",
            created_at: new Date().toISOString(),
            csvImports: [{
              id: fileConnectionId,
              fileName: file.name,
              importDate: new Date().toISOString(),
              metadata: csvMetadata
            }]
          };

          parsedCompanyData.connections = [...existingConnections, updatedConnection];

          sessionStorage.setItem('figabl_company_data', JSON.stringify(parsedCompanyData));

        }
      } catch (e) {
        console.error('Error updating company data with CSV metadata:', e);
      }
    }
  } catch (error) {
    console.error('CSV database creation error:', error);
    throw error;
  }
};

// Helper function to extract the base connection ID
function extractBaseConnectionId(connectionId: string): string {
  // If the connectionId already has a specific format (e.g., csv-import-companyId)
  if (connectionId.startsWith('csv-import-') && !connectionId.includes('_')) {
    return connectionId;
  }

  // If it has the format csv-import-companyId_filename_timestamp, extract the base part
  const parts = connectionId.split('_');

  // Return the base connection ID (csv-import-companyId)
  return parts[0];
}

// Map CSV columns to system table format - simplified since columns are now standardized
export const mapCsvColumnsToSystemTable = (data: any[], tableType: 'inventory' | 'sales'): any[] => {
  if (!data || !Array.isArray(data) || data.length === 0) {
    return [];
  }

  // Process each row, maintaining the same structure but ensuring required fields
  const mappedData = data.map(row => {
    // Add Source field if not present
    if (!row.Source) {
      row.Source = 'CSV Import';
    }
    return row;
  });

  return mappedData;
};

// Improved CSV parsing function with better header detection and description row removal
export const parseCSV = (csvContent: string): { data: any[], metadata: { description: string, headerRowIndex: number } } => {
  if (!csvContent || csvContent.trim() === '') {
    console.error('CSV content is empty');
    return { data: [], metadata: { description: '', headerRowIndex: 0 } };
  }

  // Normalize line endings and remove trailing empty lines
  const rawLines = csvContent.split(/\r?\n/);
  const lines = rawLines.map(l => l.replace(/\r$/, '')).filter((l, idx, arr) => {
    // Keep all non-empty lines and keep empty lines in the middle,
    // but drop trailing empty lines at the end of the file
    if (l.trim() !== '') return true;
    return idx < arr.length - 1;
  });

  if (lines.length === 0) {
    console.error('No lines found in CSV content');
    return { data: [], metadata: { description: '', headerRowIndex: 0 } };
  }

  /**
   * Header detection
   *
   * The tests expect a very simple, predictable behaviour:
   * - Treat everything before the first line containing a comma as metadata/description
   * - Use the first line that contains a comma as the header row
   *
   * This works well for:
   * - Simple CSVs with standard headers (first line is the header)
   * - Amazon-style reports where the first few lines are metadata, followed by a header row
   */
  let headerRowIndex = -1;
  let description = '';

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if (line.includes(',')) {
      headerRowIndex = i;
      break;
    }
  }

  // Fallback: if no comma found at all, treat the first line as header
  if (headerRowIndex === -1) {
    headerRowIndex = 0;
  }

  // Everything before the header row is considered description/metadata
  if (headerRowIndex > 0) {
    description = lines.slice(0, headerRowIndex).join('\n').trim();
  }

  const result = [];

  // Get headers from the detected header row and sanitize them for SQL safe column names
  const headers = lines[headerRowIndex].split(',').map(header => {
    // Clean and normalize the header
    const cleanHeader = header.trim().replace(/"/g, '');

    // Create a SQL-safe column name
    // Replace spaces, special chars with underscores and ensure it doesn't start with a number
    return cleanHeader.replace(/[^a-zA-Z0-9_]/g, '_').toLowerCase();
  });

  // Process data rows after the header row
  for (let i = headerRowIndex + 1; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line) {
      continue;
    }

    // Parse the CSV line properly, handling quoted values and commas within quotes
    const values = parseCSVLine(line);

    // If we don't have the right number of values, adjust the array
    const normalizedValues = normalizeValues(values, headers.length);

    const entry: Record<string, string> = {};

    for (let j = 0; j < headers.length; j++) {
      if (headers[j]) {
        // Always store as string to avoid numeric parsing issues
        entry[headers[j]] = String(normalizedValues[j] || '');
      }
    }

    if (Object.keys(entry).length > 0) {
      result.push(entry);
    }
  }

  return {
    data: result,
    metadata: {
      description,
      headerRowIndex
    }
  };
};

// Helper function to parse a CSV line correctly, handling quotes
export const parseCSVLine = (line: string): string[] => {
  const values: string[] = [];
  let currentValue = '';
  let inQuotes = false;

  for (let j = 0; j < line.length; j++) {
    const char = line[j];

    if (char === '"' && (j === 0 || line[j - 1] !== '\\')) {
      inQuotes = !inQuotes;
    } else if (char === ',' && !inQuotes) {
      values.push(currentValue.trim().replace(/^"|"$/g, ''));
      currentValue = '';
    } else {
      currentValue += char;
    }
  }

  // Add the last value
  values.push(currentValue.trim().replace(/^"|"$/g, ''));

  // If parsing failed (e.g., quotes not matched properly), fall back to simple split
  if (values.some(v => v.includes('"') && !v.startsWith('"') && !v.endsWith('"'))) {
    return line.split(',').map(value => value.trim().replace(/^"|"$/g, ''));
  }

  return values;
};

// Helper function to normalize array length
const normalizeValues = (values: string[], expectedLength: number): string[] => {
  const result = [...values];

  // Add empty strings for missing values
  while (result.length < expectedLength) {
    result.push('');
  }

  // Remove extra values
  while (result.length > expectedLength) {
    result.pop();
  }

  return result;
};
